import React, {useMemo} from 'react'
import styled from 'styled-components'
import {IMAGE_ASSET} from 'consts'
import {useTranslation} from 'i18n'
import {getBorder, getFontFamily} from 'utils'
import {Image, Paragraph, ParsedText} from 'common/components'
import convertUnit from 'lib/unit'
import {FototreeFootprintListItemProps} from './FototreeFootprintProps'

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: ${convertUnit(8)} ${convertUnit(20)};
  gap: ${convertUnit(20)};
`

const StyledItemInfoContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${convertUnit(2.5)};
  justify-content: center;
`

const StyledImage = styled(Image)`
  border: ${({theme}) => getBorder(1, 'solid', theme.gray_1)};
  border-radius: ${convertUnit(8)};
  max-width: ${convertUnit(56)};
  width: 100%;
  aspect-ratio: 0.75;
  object-fit: contain;
`

const StyledParagraph = styled(Paragraph)`
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-all;
`

export default function FototreeFootprintListItem({
  data,
  onClickItem,
}: FototreeFootprintListItemProps) {
  const defaultImage = IMAGE_ASSET('tree', 'default.png')
  const {translate} = useTranslation()

  const handleRenderImage = useMemo(
    () => (
      <StyledImage
        src={data.photo}
        fallbackSrc={defaultImage}
        alt={data.name}
      />
    ),
    [data.name, data.photo, defaultImage],
  )

  const handleRenderInfo = useMemo(
    () => (
      <StyledItemInfoContainer>
        <StyledParagraph fontSize="m" fontWeight="bold">
          {data.name}
        </StyledParagraph>
        <Paragraph color="primary_5">
          <ParsedText
            parse={[
              {
                pattern: /<bold>.*?<\/bold>/,
                style: {fontFamily: getFontFamily('bold')},
                renderText: (text) => text.replace(/<bold>|<\/bold>/g, ''),
              },
            ]}>
            {translate('tree:leafCount', {
              count: data.leaf_count,
            })}
          </ParsedText>
        </Paragraph>
      </StyledItemInfoContainer>
    ),
    [data.leaf_count, data.name, translate],
  )

  return (
    <StyledContainer onClick={() => onClickItem(data)}>
      {handleRenderImage}
      {handleRenderInfo}
    </StyledContainer>
  )
}
